import { useCallback } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import Link from '@/components/atoms/Link/Link';
import { WarningAlert } from '@/components/atoms/WarningAlert/WarningAlert';
import { MODAL_DELETE, MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import { AI_AGENT_REMINDER_FORM_ID } from '@/modules/aiAgents/constants';
import { clearDialogAlerts } from '@/redux/dialogAlerts/actions';
import { selectDialogAlerts } from '@/redux/dialogAlerts/selectors';
import { selectSelectedNode } from '@/redux/dialogs/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import { removeNode, removeRequisite } from '@/redux/nodes/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';

import useBrains from './useBrains';
import useNodesConnected, { buildUsedNodeReferences } from './useNodesConnected';

const useMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  const { findUsedNodes } = useNodesConnected();

  const selectedNodeId = useSelector(
    (state: RootState) => state.nodes.selectedNodeId,
    shallowEqual
  );

  const dialogErrors = useSelector(selectDialogAlerts);
  const selectedNode = useSelector(selectSelectedNode);

  const reminders = brain?.guidelines?.reminders || [];
  const isNodeUsedInReminders = reminders.find((r) => r.node_id === selectedNodeId);
  // Guards deletion of the brain's starting node so a stored starting_node_id
  // can't dead-end node deletion.
  const isStartingNode =
    Boolean(brain?.starting_node_id) && brain?.starting_node_id === selectedNodeId;

  const deleteRequisite = useCallback(
    (nodeId: string, index: number, saveAs?: string) => {
      const deleteProps = {
        subtitle: <Trans i18nKey="dialog.delete_requisite" values={[saveAs || 'question']} />,
        confirm: false,
        onDelete: () => {
          dispatch(removeRequisite({ nodeId, index }));

          if (dialogErrors.length) {
            dispatch(clearDialogAlerts({ nodeId, index }));
          }

          dispatch(popModal());
        },
        secondaryButtonText: t('common.cancel'),
      };
      dispatch(pushModal(MODAL_DELETE, deleteProps));
    },
    [dispatch, dialogErrors.length, t]
  );

  const deleteNode = useCallback(
    (nodeId: string, name: string) => {
      const usedNodes = findUsedNodes(nodeId);
      if (usedNodes && !isEmpty(usedNodes)) {
        const references = buildUsedNodeReferences(usedNodes, slug, brainId, t);
        const warning = (
          <AlertWithReferences
            references={references}
            referenceProp="href"
            onRefClick={(href: string) => {
              dispatch(popModal());
              navigate(href);
            }}
          >
            <Trans i18nKey="dialog.delete_node" values={[name]} />
          </AlertWithReferences>
        );
        const warnProps = {
          title: t('common.warning'),
          children: warning,
          primaryButtonText: t('common.close'),
        };

        dispatch(pushModal(MODAL_WARN, warnProps));
      } else if (isNodeUsedInReminders) {
        const warning = (
          <WarningAlert>
            <p>
              <Trans
                i18nKey="dialog.delete_node_with_reminders"
                values={[selectedNode?.name]}
                components={{
                  link1: (
                    <Link
                      onClick={() => {
                        dispatch(popModal());
                        navigate(
                          `/${slug}/ai-agents/${brainId}/overview#${AI_AGENT_REMINDER_FORM_ID}`
                        );
                      }}
                    />
                  ),
                }}
              />
            </p>
          </WarningAlert>
        );

        const warnProps = {
          title: t('common.warning'),
          children: warning,
          primaryButtonText: t('common.close'),
        };

        dispatch(pushModal(MODAL_WARN, warnProps));
      } else if (isStartingNode) {
        const warning = (
          <WarningAlert>
            <p>
              <Trans
                i18nKey="dialog.delete_node_with_starting_node"
                values={[selectedNode?.name]}
                components={{
                  link1: (
                    <Link
                      onClick={() => {
                        dispatch(popModal());
                        navigate(`/${slug}/ai-agents/${brainId}/settings#conversation-start`);
                      }}
                    />
                  ),
                }}
              />
            </p>
          </WarningAlert>
        );

        const warnProps = {
          title: t('common.warning'),
          children: warning,
          primaryButtonText: t('common.close'),
        };

        dispatch(pushModal(MODAL_WARN, warnProps));
      } else {
        const deleteProps = {
          title: t('dialog.delete_node_title'),
          subtitle: <Trans i18nKey="dialog.delete_node_warn" values={[name]} />,
          onDelete: () => {
            dispatch(removeNode({ nodeId }));

            if (dialogErrors.length) {
              // Clears its errors
              dispatch(clearDialogAlerts({ id: nodeId }));
              // Clears its children errors
              dispatch(clearDialogAlerts({ nodeId }));
            }

            const lastAction = selectedNode?.actions?.[selectedNode?.actions.length - 1];
            // Delete Reminder node, when the current node has a Reminder action
            if (lastAction?.type === 'reminder') {
              dispatch(removeNode({ nodeId: lastAction.trigger_node_id }));
            }

            dispatch(popModal());
          },
          secondaryButtonText: t('common.cancel'),
        };

        dispatch(pushModal(MODAL_DELETE, deleteProps));
      }
    },
    [
      dispatch,
      findUsedNodes,
      navigate,
      t,
      slug,
      brainId,
      dialogErrors.length,
      isNodeUsedInReminders,
      isStartingNode,
      selectedNode?.actions,
      selectedNode?.name,
    ]
  );

  return { deleteNode, deleteRequisite };
};

export default useMenu;
